import type Account from '@/data/account/model';
import type User from '@/data/user/model';
import { setEmployeeOnboardingItemCompleted } from '@/onboarding/actions/setEmployeeOnboardingItemCompleted';
import { EmployeeOnboardingItems, EmployeeOnboardingStatus } from '@/onboarding/constants';
import GuideHighlight from '@/onboarding/modules/guides/components/GuideHighlight';
import { setEmployeeOnboardingStatus } from '@/onboarding/reducer';
import { getEmployeeOnboardingStatus } from '@/onboarding/selectors';
import { logout, switchWorkplace } from '@/shared/auth/actions';
import { MenuItem } from '@/shared/components/nav/MenuItem';
import MenuItems from '@/shared/components/nav/MenuItems';
import AvatarImg from '@/shared/ui/AvatarImg';
import { UpgradePopover } from '@/shared/upsell/popovers/UpgradePopover';
import { posthogCapture } from '@/shared/vendor/posthog';
import { useWiwDispatch, useWiwSelector } from '@/store';

type AvatarMenuProps = {
  authUser: User;
  authAccount: Account;
};

type TrackingType = 'profile_settings' | 'my_schedule' | 'my_availability' | 'switch_workplaces' | 'logout';

const AvatarMenuItem = ({ authUser, authAccount }: AvatarMenuProps) => {
  const dispatch = useWiwDispatch();
  const employeeOnboardingStatus = useWiwSelector(getEmployeeOnboardingStatus);
  const isAlertGuidedStep = employeeOnboardingStatus === EmployeeOnboardingStatus.AlertPreferences;

  const dismissGuide = () => {
    dispatch(setEmployeeOnboardingStatus(EmployeeOnboardingStatus.Idle));
  };

  const completeGuide = () => {
    dispatch(setEmployeeOnboardingStatus(EmployeeOnboardingStatus.Idle));
    dispatch(setEmployeeOnboardingItemCompleted(EmployeeOnboardingItems.AlertPreferencesCompleted));
    posthogCapture('employee_onboarding.notification_preferences.click.profile');
  };

  function trackOnInteraction(type: TrackingType) {
    posthogCapture(`top_nav.profile.${type}`);
  }

  function handleSwitchWorkplace() {
    switchWorkplace();
    trackOnInteraction('switch_workplaces');
  }

  function handleLogout() {
    logout(false);
    trackOnInteraction('logout');
  }

  return (
    <UpgradePopover>
      <MenuItem
        className="avatar menu-icon"
        hideLabel="tablet"
        id="avatarmenu"
        dropdown
        forceActive={isAlertGuidedStep}
        label={
          <abbr>
            <AvatarImg alt="menu-avatar" className="menu-avatar" user={authUser} width={24} height={24} />
            <span className="nav-item-label">{authUser.first_name}</span>
          </abbr>
        }
      >
        <MenuItems dropdownMenu>
          <GuideHighlight
            isActive={isAlertGuidedStep}
            highlightId="alerts-guide-highlight"
            onHighlightClick={completeGuide}
            showPopover
            popoverText="Go to “Profile & Settings” to adjust your notification preferences."
            popoverPosition="left-start"
            popoverOffset={{ mainAxis: 10, crossAxis: 10 }}
            hideOverlay
            dismissOnOutsideClick
            onDismiss={dismissGuide}
          >
            <MenuItem
              icon="my-profile"
              label="Profile & Settings"
              to="/profile"
              data-testid={isAlertGuidedStep ? 'eo-set-alerts-select' : undefined}
              onClick={() => trackOnInteraction('profile_settings')}
            />
          </GuideHighlight>

          {authUser.canSupervise() && (
            <MenuItem
              icon="my-schedule"
              label="My Schedule"
              show={authAccount.hasScheduling()}
              to="/myschedule"
              disabled={isAlertGuidedStep}
              data-testid="top-nav-profile-my-schedule"
              onClick={() => trackOnInteraction('my_schedule')}
            />
          )}

          <MenuItem
            icon="my-availability"
            label="My Availability"
            show={
              authAccount.hasScheduling() &&
              (authAccount.getSettings('availability.enabled', false) || authUser.canManage())
            }
            to="/availability"
            disabled={isAlertGuidedStep}
            data-testid="top-nav-profile-my-availability"
            onClick={() => trackOnInteraction('my_availability')}
          />

          <MenuItem
            icon="workplace"
            label="Switch Workplaces"
            onClick={handleSwitchWorkplace}
            disabled={isAlertGuidedStep}
            data-testid="top-nav-profile-switch-workplace"
          />

          <MenuItem
            data-testid="top-nav-profile-logout"
            onClick={handleLogout}
            icon="logout"
            label="Logout"
            disabled={isAlertGuidedStep}
          />
        </MenuItems>
      </MenuItem>
    </UpgradePopover>
  );
};

export default AvatarMenuItem;
